/**
 * Studio Booking · Email settings tab — extracted Oct 2026.
 * Owns workspace-level email templates, admin recipient lists,
 * digest config, Interakt WhatsApp templates & the chips email input.
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Bell, CalendarDays, Copy, ExternalLink, Eye, MessageCircle, RefreshCw, Send, X as XIcon } from "lucide-react";


// Oct 2026 · R290 · Suggested WhatsApp (Interakt) template bodies.
// These are the exact strings a studio owner should paste into the
// Interakt dashboard when creating the three approved templates.
// Variable order MUST match the backend fan-out in
// `_send_booking_whatsapp` so {{N}} resolves correctly:
//   {{1}}  customer name          {{2}}  booking ref       {{3}}  slot (date + time)
//   {{4}}  studio name            {{5}}  tracking URL (also used as button URL)
//   {{6}}  booking status         {{7}}  space name        {{8}}  amount total (₹)
//   {{9}}  amount due (₹)         {{10}} package name      {{11}} support phone
// Variables 1-5 are the original "classic" set — existing templates
// keep working. 6-11 are optional extensions admins can bring in as
// they rebuild templates for richer customer messages.
const WA_SUGGESTED_TEMPLATES = {
  customer_confirmation: {
    label: "Customer booking confirmation",
    body:
`Hi {{1}}, your booking {{2}} at {{4}} is {{6}} ✓

Space: {{7}}
Slot: {{3}}
Total: {{8}}
Balance due: {{9}}

Track your booking anytime: {{5}}

Reply here if you need to reschedule — we're happy to help.`,
  },
  customer_reminder: {
    label: "Customer booking reminder",
    body:
`Hi {{1}}, quick reminder — your shoot at {{4}} ({{7}}) is coming up.

Booking: {{2}} · Status: {{6}}
Slot: {{3}}
Balance due at check-in: {{9}}

Need directions or a reschedule? Open your tracking page: {{5}}
Call us: {{11}}`,
  },
  internal_notify: {
    label: "Internal team notification",
    body:
`📸 {{6}} booking at {{4}} — {{7}}

Ref: {{2}}
Customer: {{1}}
Slot: {{3}}
Package: {{10}}
Total: {{8}} · Due: {{9}}

Admin link: {{5}}`,
  },
};

// Feb 2026 · Variable reference list shown under each template body
// editor. Order MUST match the backend positional vars in
// `_send_booking_whatsapp`. Shown as copy-on-click chips so admins
// can insert a {{N}} without typing. `sample` is the realistic
// placeholder value rendered in the Live Preview bubble so admins
// see exactly what the final WA message will look like with real
// data substituted in — no need to send a test to themselves.
const WA_VARIABLE_LEGEND = [
  { token: "{{1}}",  label: "Customer name",     sample: "Alex Mehta" },
  { token: "{{2}}",  label: "Booking ref",       sample: "#1234" },
  { token: "{{3}}",  label: "Slot (date & time)", sample: "Sat, 15 Feb · 10:00 AM – 2:00 PM" },
  { token: "{{4}}",  label: "Studio name",       sample: "Story Studios Andheri" },
  { token: "{{5}}",  label: "Tracking URL",      sample: "https://acquilix.com/track/abc" },
  { token: "{{6}}",  label: "Booking status",    sample: "CONFIRMED" },
  { token: "{{7}}",  label: "Space name",        sample: "Main Floor" },
  { token: "{{8}}",  label: "Amount total",      sample: "\u20B95,000" },
  { token: "{{9}}",  label: "Amount due",        sample: "\u20B92,500" },
  { token: "{{10}}", label: "Package name",      sample: "Starter Package" },
  { token: "{{11}}", label: "Support phone",     sample: "+91 98123 45678" },
];

// Feb 2026 · Interakt's "new template" page in the admin dashboard.
// They don't accept query-string prefill for the message body (we
// double-checked their docs — only the manual paste works), so the
// Submit-to-Interakt shortcut copies the body to clipboard and opens
// this URL in a new tab. Admins land one click away from paste.
const INTERAKT_NEW_TEMPLATE_URL = "https://app.interakt.ai/campaigns/message-templates/new";

// Feb 2026 · Render a template body with the sample values plugged in,
// so the live preview reads like the real WA message instead of raw
// {{N}} tokens.
function renderWithSamples(body) {
  if (!body) return "";
  let out = String(body);
  // Replace longest tokens first so {{10}}/{{11}} don't collide with {{1}}.
  const ordered = [...WA_VARIABLE_LEGEND].sort(
    (a, b) => b.token.length - a.token.length);
  for (const v of ordered) {
    out = out.split(v.token).join(v.sample);
  }
  return out;
}

async function copyToClipboard(text, successMsg) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(successMsg || "Copied");
    return true;
  } catch {
    toast.error("Couldn't copy — select the text and copy manually");
    return false;
  }
}

async function copyWaTemplateBody(bodyText) {
  if (!bodyText) return;
  await copyToClipboard(bodyText, "Template body copied — paste it into your Interakt dashboard");
}


export function StudioEmailSettings({ ws, canManage }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [data, setData] = useState(null);
  const [selectedKind, setSelectedKind] = useState("received");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  // Oct 2026 · WhatsApp test-send diagnostic state.
  const [waTestKind, setWaTestKind] = useState("confirmation");
  const [waTestPhone, setWaTestPhone] = useState("");
  const [waTesting, setWaTesting] = useState(false);
  const [waTestResult, setWaTestResult] = useState(null);
  const sendWaTest = async () => {
    if (!waTestPhone.trim()) return;
    setWaTesting(true);
    setWaTestResult(null);
    try {
      const { data: r } = await api.post(`/workspaces/${ws}/studio/wa-test`, {
        kind: waTestKind,
        to_phone: waTestPhone.trim(),
      });
      setWaTestResult(r);
      if (r.ok) toast.success("Interakt accepted the message");
      else toast.error(r.reason ? `WA not sent: ${r.reason}` : "WA not sent — see diagnostic below");
    } catch (e) {
      const detail = e?.response?.data || { reason: apiErrorMessage(e, "Request failed") };
      setWaTestResult({ ok: false, ...detail });
      toast.error(apiErrorMessage(e, "WA test failed"));
    } finally {
      setWaTesting(false);
    }
  };
  const [testTo, setTestTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get(`/workspaces/${ws}/studio/email-settings`);
      setData(r);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load email settings"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (ws) load(); /* eslint-disable-next-line */ }, [ws]);

  const patchTemplate = (kind, key, value) => {
    setData((d) => {
      const existing = d.studio_email_templates?.[kind];
      const defaults = d.default_templates?.[kind] || {};
      // Seed the row with defaults the first time an admin touches a
      // kind so they see a good starting point instead of a blank
      // subject / body. Once they've saved once, `existing` wins.
      const base = existing || {
        subject: defaults.subject || "",
        html: defaults.html || "",
        enabled_customer: true, enabled_admin: true,
      };
      return {
        ...d,
        studio_email_templates: {
          ...(d.studio_email_templates || {}),
          [kind]: { ...base, [key]: value },
        },
      };
    });
  };
  const seedDefaults = (kind) => {
    setData((d) => {
      const defaults = d.default_templates?.[kind] || {};
      return {
        ...d,
        studio_email_templates: {
          ...(d.studio_email_templates || {}),
          [kind]: {
            subject: defaults.subject || "",
            html: defaults.html || "",
            enabled_customer: true, enabled_admin: true,
          },
        },
      };
    });
    toast.success("Default template loaded — edit + save when ready");
  };
  const resetTemplate = (kind) => {
    setData((d) => {
      const next = { ...(d.studio_email_templates || {}) };
      delete next[kind];
      return { ...d, studio_email_templates: next };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/workspaces/${ws}/studio/email-settings`, {
        studio_admin_emails: data.studio_admin_emails || [],
        enquiry_admin_emails: data.enquiry_admin_emails || [],
        studio_email_templates: data.studio_email_templates || {},
        studio_daily_digest: data.studio_daily_digest || { enabled: false, recipients: [] },
        // Oct 2026 · Studio WhatsApp (Interakt) templates + internal
        // notify phones. Carried in the same PUT so admins save every
        // comms knob in a single click.
        studio_wa_settings: data.studio_wa_settings ? {
          wa_template_customer_confirmation: data.studio_wa_settings.wa_template_customer_confirmation || "",
          wa_template_customer_reminder: data.studio_wa_settings.wa_template_customer_reminder || "",
          wa_template_internal_notify: data.studio_wa_settings.wa_template_internal_notify || "",
          wa_internal_notify_phones: data.studio_wa_settings.wa_internal_notify_phones || [],
          wa_language_code: data.studio_wa_settings.wa_language_code || "en",
          // Feb 2026 · Admin-tuned template bodies — the exact copy
          // the studio owner will paste into Interakt. Saved so the
          // tweak survives page reloads and admins can hand it to a
          // teammate / Interakt submission later.
          wa_template_body_customer_confirmation: data.studio_wa_settings.wa_template_body_customer_confirmation || "",
          wa_template_body_customer_reminder: data.studio_wa_settings.wa_template_body_customer_reminder || "",
          wa_template_body_internal_notify: data.studio_wa_settings.wa_template_body_internal_notify || "",
        } : undefined,
      });
      toast.success("Email settings saved");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async () => {
    try {
      const tpl = data.studio_email_templates?.[selectedKind] || {};
      const { data: r } = await api.post(`/workspaces/${ws}/studio/email-settings/preview`, {
        kind: selectedKind,
        subject: tpl.subject || "",
        html: tpl.html || "",
      });
      setPreview(r);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Preview failed"));
    }
  };

  const testSend = async () => {
    const to = (testTo || "").trim();
    if (!to || !to.includes("@")) { toast.error("Enter a valid email address"); return; }
    try {
      await api.post(`/workspaces/${ws}/studio/email-settings/test-send`, {
        kind: selectedKind, to,
      });
      toast.success(`Test email queued to ${to}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Test send failed"));
    }
  };

  const sendDigestNow = async () => {
    try {
      const { data: r } = await api.post(`/workspaces/${ws}/studio/email-settings/send-digest`);
      if (r.skipped === "digest_disabled") toast.error("Digest is disabled — enable it first");
      else if (r.skipped === "no_recipients") toast.error("No recipients configured");
      else toast.success(`Digest sent to ${r.sent || 0} recipient(s) · ${r.shoot_count} shoot(s)`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Digest send failed"));
    }
  };

  if (loading || !data) {
    return <div className="p-6 text-xs text-muted-foreground">Loading email settings…</div>;
  }

  const tpl = data.studio_email_templates?.[selectedKind] || {};
  const currentKindMeta = (data.kinds || []).find((k) => k.kind === selectedKind) || {};

  return (
    <div className="space-y-6" data-testid="sb-emails-tab">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4 justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-amber-700/80 font-semibold mb-1">Studio · Emails</div>
          <h2 className="text-xl font-semibold" style={{ fontFamily: `"Cormorant Garamond", Georgia, serif` }}>Email templates & notifications</h2>
          <p className="text-[11px] text-muted-foreground mt-1 max-w-xl">
            Customize the emails customers and your team receive for every booking event. Templates fall back to a warm-luxe default when left blank. Use <code className="font-mono text-[10px] bg-muted px-1">{"{{customer_name}}"}</code>-style tokens for dynamic content.
          </p>
        </div>
        <Button size="sm" onClick={save} disabled={!canManage || saving}
                data-testid="sb-emails-save"
                className="rounded-none uppercase tracking-widest text-[10px]">
          {saving ? "Saving…" : "Save all changes"}
        </Button>
      </div>

      {/* Admin notify list */}
      <div className="border border-border p-4">
        <div className="flex items-center gap-2 mb-2">
          <Bell className="w-4 h-4 text-amber-700" />
          <div className="text-[10px] uppercase tracking-widest font-semibold">Booking admin CC</div>
        </div>
        <p className="text-[11px] text-muted-foreground mb-2">These addresses get CC'd on every booking-related email (received / confirmed / cancelled / etc.). Great for the front-desk inbox or a shared alias.</p>
        <ChipsEmailInput value={data.studio_admin_emails || []}
                          onChange={(v) => setData((d) => ({ ...d, studio_admin_emails: v }))}
                          placeholder="ops@yourstudio.com, front-desk@…"
                          testid="sb-emails-admins"
                          disabled={!canManage} />
      </div>

      {/* Feb 2026 · R280.8 · Enquiry admin CC — separate from booking
          list so front-desk / bookings-only inboxes don't get every
          enquiry ping. */}
      <div className="border border-border p-4">
        <div className="flex items-center gap-2 mb-2">
          <MessageCircle className="w-4 h-4 text-amber-700" />
          <div className="text-[10px] uppercase tracking-widest font-semibold">Enquiries admin CC</div>
        </div>
        <p className="text-[11px] text-muted-foreground mb-2">Extra addresses to notify whenever a public enquiry lands. The assignee + workspace owner always receive it — this list is for shared ops / sales inboxes.</p>
        <ChipsEmailInput value={data.enquiry_admin_emails || []}
                          onChange={(v) => setData((d) => ({ ...d, enquiry_admin_emails: v }))}
                          placeholder="sales@yourstudio.com, hello@…"
                          testid="sb-emails-enquiry-admins"
                          disabled={!canManage} />
      </div>

      {/* Daily digest */}
      <div className="border border-border p-4">
        <div className="flex items-center gap-2 mb-2">
          <CalendarDays className="w-4 h-4 text-amber-700" />
          <div className="text-[10px] uppercase tracking-widest font-semibold">Daily shoot digest</div>
          <label className="ml-auto inline-flex items-center gap-2 text-[11px]">
            <input type="checkbox"
                     checked={!!data.studio_daily_digest?.enabled}
                     data-testid="sb-emails-digest-enabled"
                     onChange={(e) => setData((d) => ({
                       ...d, studio_daily_digest: {
                         ...(d.studio_daily_digest || {}), enabled: e.target.checked,
                       },
                     }))}
                     disabled={!canManage} />
            Send every morning at 8 AM IST
          </label>
        </div>
        <p className="text-[11px] text-muted-foreground mb-2">A summary of the day's confirmed shoots, with customers, slots, spaces and totals. Ships to the admin CC list above <em>plus</em> anyone you add here.</p>
        <ChipsEmailInput value={data.studio_daily_digest?.recipients || []}
                          onChange={(v) => setData((d) => ({
                            ...d, studio_daily_digest: { ...(d.studio_daily_digest || {}), recipients: v },
                          }))}
                          placeholder="director@yourstudio.com, …"
                          testid="sb-emails-digest-recipients"
                          disabled={!canManage} />
        <Button size="sm" variant="outline"
                onClick={sendDigestNow} disabled={!canManage}
                data-testid="sb-emails-digest-send-now"
                className="rounded-none h-7 text-[10px] uppercase tracking-widest mt-3">
          <Send className="w-3 h-3 mr-1" /> Send today's digest now
        </Button>
      </div>

      {/* Oct 2026 · WhatsApp (Interakt) template config. Separate card
          below the email panels so the two channels stay visually
          distinct. All fields optional — leave a template name blank
          to fall back to free-form session text. */}
      <div className="border border-border p-4" data-testid="sb-wa-settings-panel">
        <h4 className="text-xs uppercase tracking-widest text-muted-foreground mb-1">WhatsApp templates (Interakt)</h4>
        <p className="text-[11px] text-muted-foreground mb-3">
          Each template name must match the exact approved template in your Interakt dashboard.
          Variables are sent in a fixed order — reference them below.
          <span className="block mt-1">
            Tap <b>Copy body</b> next to each field to grab a ready-to-paste template — then paste it into Interakt&rsquo;s template editor and submit for approval.
          </span>
          {!data.studio_wa_settings?.interakt_configured && (
            <span className="block mt-1 text-amber-700 font-medium">
              ⚠ Interakt isn&rsquo;t connected yet — set it up under Integrations first.
            </span>
          )}
        </p>
        <div className="space-y-4 mb-3">
          <WaTemplateCard
            label="Customer booking confirmation"
            testid="customer-confirmation"
            placeholderName="e.g. booking_confirmed_v1"
            placeholderBody="Hi {{1}}, your booking {{2}} at {{4}} is {{6}} ✓ …"
            nameValue={data.studio_wa_settings?.wa_template_customer_confirmation || ""}
            onNameChange={(v) => setData((d) => ({
              ...d, studio_wa_settings: { ...(d.studio_wa_settings || {}), wa_template_customer_confirmation: v },
            }))}
            bodyValue={data.studio_wa_settings?.wa_template_body_customer_confirmation
                       || WA_SUGGESTED_TEMPLATES.customer_confirmation.body}
            onBodyChange={(v) => setData((d) => ({
              ...d, studio_wa_settings: { ...(d.studio_wa_settings || {}), wa_template_body_customer_confirmation: v },
            }))}
            defaultBody={WA_SUGGESTED_TEMPLATES.customer_confirmation.body}
            canManage={canManage}
          />
          <WaTemplateCard
            label="Customer booking reminder"
            testid="customer-reminder"
            placeholderName="e.g. booking_reminder_v1"
            placeholderBody="Hi {{1}}, quick reminder — your shoot at {{4}} …"
            nameValue={data.studio_wa_settings?.wa_template_customer_reminder || ""}
            onNameChange={(v) => setData((d) => ({
              ...d, studio_wa_settings: { ...(d.studio_wa_settings || {}), wa_template_customer_reminder: v },
            }))}
            bodyValue={data.studio_wa_settings?.wa_template_body_customer_reminder
                       || WA_SUGGESTED_TEMPLATES.customer_reminder.body}
            onBodyChange={(v) => setData((d) => ({
              ...d, studio_wa_settings: { ...(d.studio_wa_settings || {}), wa_template_body_customer_reminder: v },
            }))}
            defaultBody={WA_SUGGESTED_TEMPLATES.customer_reminder.body}
            canManage={canManage}
          />
          <WaTemplateCard
            label="Internal team notification"
            testid="internal-notify"
            placeholderName="e.g. ops_new_booking_v1"
            placeholderBody="📸 {{6}} booking at {{4}} — {{7}} …"
            nameValue={data.studio_wa_settings?.wa_template_internal_notify || ""}
            onNameChange={(v) => setData((d) => ({
              ...d, studio_wa_settings: { ...(d.studio_wa_settings || {}), wa_template_internal_notify: v },
            }))}
            bodyValue={data.studio_wa_settings?.wa_template_body_internal_notify
                       || WA_SUGGESTED_TEMPLATES.internal_notify.body}
            onBodyChange={(v) => setData((d) => ({
              ...d, studio_wa_settings: { ...(d.studio_wa_settings || {}), wa_template_body_internal_notify: v },
            }))}
            defaultBody={WA_SUGGESTED_TEMPLATES.internal_notify.body}
            canManage={canManage}
          />
          <div className="max-w-[160px]">
            <Label className="text-xs">Language code</Label>
            <Input
              placeholder="en"
              value={data.studio_wa_settings?.wa_language_code || ""}
              onChange={(e) => setData((d) => ({
                ...d, studio_wa_settings: { ...(d.studio_wa_settings || {}), wa_language_code: e.target.value },
              }))}
              className="rounded-none h-8 text-xs"
              disabled={!canManage}
              data-testid="sb-wa-tpl-language"
            />
          </div>
        </div>
        {/* Oct 2026 · Diagnostic test send — fires the configured
            template to a chosen number and surfaces the Interakt
            response inline. Essential for debugging "why didn't my
            WA message arrive after a booking?" without digging into
            server logs. */}
        <div className="mb-3 border border-dashed border-border p-3 bg-muted/20">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
            Test WhatsApp send
          </div>
          <p className="text-[10px] text-muted-foreground mb-2">
            Pick a kind, enter a WhatsApp-registered number (full international format), and we&apos;ll push the saved template with sample variables. The exact Interakt response is shown so you can confirm the template is approved and the API key works.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[180px]">
              <Label className="text-[10px] text-muted-foreground">Template</Label>
              <select
                value={waTestKind}
                onChange={(e) => setWaTestKind(e.target.value)}
                className="w-full h-8 rounded-none text-xs border border-input bg-background px-2"
                disabled={!canManage}
                data-testid="sb-wa-test-kind"
              >
                <option value="confirmation">Customer confirmation</option>
                <option value="reminder">Customer reminder</option>
                <option value="internal_notify">Internal team notify</option>
              </select>
            </div>
            <div className="min-w-[200px] flex-1">
              <Label className="text-[10px] text-muted-foreground">Send to</Label>
              <Input
                value={waTestPhone}
                onChange={(e) => setWaTestPhone(e.target.value)}
                placeholder="+919812345678"
                className="rounded-none h-8 text-xs"
                disabled={!canManage}
                data-testid="sb-wa-test-phone"
              />
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-none h-8"
              disabled={!canManage || waTesting || !waTestPhone.trim()}
              onClick={sendWaTest}
              data-testid="sb-wa-test-send"
            >
              {waTesting ? "Sending…" : "Send test"}
            </Button>
          </div>
          {waTestResult && (
            <div className={`mt-2 border px-2 py-1.5 text-[11px] font-mono ${waTestResult.ok ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-rose-300 bg-rose-50 text-rose-900"}`}
                 data-testid="sb-wa-test-result">
              <div className="font-semibold mb-0.5">
                {waTestResult.ok ? "✓ Interakt accepted" : "✗ Not sent"}
                {waTestResult.reason ? ` · ${waTestResult.reason}` : ""}
              </div>
              {waTestResult.hint && (
                <div className="text-[10px] opacity-80 mb-1">{waTestResult.hint}</div>
              )}
              <pre className="whitespace-pre-wrap break-all text-[10px] opacity-80 m-0">
                {JSON.stringify(waTestResult, null, 2)}
              </pre>
            </div>
          )}
        </div>
        <Label className="text-xs">Internal team phone numbers (max 25)</Label>
        <p className="text-[10px] text-muted-foreground mb-1">
          Use full international format (e.g. +919812345678). Each number gets a WhatsApp ping on new &amp; confirmed bookings.
        </p>
        <ChipsEmailInput
          value={data.studio_wa_settings?.wa_internal_notify_phones || []}
          onChange={(v) => setData((d) => ({
            ...d, studio_wa_settings: { ...(d.studio_wa_settings || {}), wa_internal_notify_phones: v },
          }))}
          placeholder="Add phone and press Enter…"
          testid="sb-wa-internal-phones"
          disabled={!canManage}
          validator={(s) => /^\+?[0-9][0-9\s\-()]{6,}$/.test(s.trim())}
          normalizer={(s) => s.replace(/[^0-9+]/g, "")}
        />
      </div>

      {/* Template editor */}
      <div className="border border-border">
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr]">
          {/* Left sidebar — event kinds */}
          <div className="border-b md:border-b-0 md:border-r border-border bg-muted/20">
            {(data.kinds || []).map((k) => {
              const on = data.studio_email_templates?.[k.kind];
              const active = selectedKind === k.kind;
              return (
                <button key={k.kind}
                        onClick={() => setSelectedKind(k.kind)}
                        data-testid={`sb-emails-kind-${k.kind}`}
                        className={`w-full text-left px-4 py-3 border-b border-border/60 transition-colors ${active ? "bg-amber-50" : "hover:bg-muted/40"}`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${on ? "bg-emerald-500" : "bg-neutral-300"}`}
                          title={on ? "Custom template" : "Using default"} />
                    <span className="text-xs font-semibold">{k.label}</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{k.hint}</div>
                </button>
              );
            })}
          </div>
          {/* Right panel — editor */}
          <div className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-amber-700">{currentKindMeta.label}</div>
                <div className="text-[11px] text-muted-foreground">{currentKindMeta.hint}</div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-1.5 text-[11px] whitespace-nowrap">
                  <input type="checkbox" checked={tpl.enabled_customer !== false}
                             data-testid={`sb-emails-toggle-customer-${selectedKind}`}
                             onChange={(e) => patchTemplate(selectedKind, "enabled_customer", e.target.checked)}
                             disabled={!canManage} />
                  Email customer
                </label>
                <label className="inline-flex items-center gap-1.5 text-[11px] whitespace-nowrap">
                  <input type="checkbox" checked={tpl.enabled_admin !== false}
                             data-testid={`sb-emails-toggle-admin-${selectedKind}`}
                             onChange={(e) => patchTemplate(selectedKind, "enabled_admin", e.target.checked)}
                             disabled={!canManage} />
                  Notify admins
                </label>
                <Button size="sm" variant="outline"
                          onClick={openPreview}
                          data-testid={`sb-emails-preview-${selectedKind}`}
                          className="rounded-none h-7 text-[10px] uppercase tracking-widest">
                  <Eye className="w-3 h-3 mr-1" /> Preview
                </Button>
                <Button size="sm" variant="ghost"
                          onClick={() => seedDefaults(selectedKind)}
                          disabled={!canManage}
                          data-testid={`sb-emails-load-default-${selectedKind}`}
                          className="rounded-none h-7 text-[10px] uppercase tracking-widest text-primary">
                  Load default
                </Button>
                <Button size="sm" variant="ghost"
                          onClick={() => resetTemplate(selectedKind)}
                          disabled={!canManage || !data.studio_email_templates?.[selectedKind]}
                          data-testid={`sb-emails-reset-${selectedKind}`}
                          className="rounded-none h-7 text-[10px] uppercase tracking-widest text-muted-foreground">
                  <RefreshCw className="w-3 h-3 mr-1" /> Reset
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest">Subject line</Label>
              <Input value={tpl.subject || ""} onChange={(e) => patchTemplate(selectedKind, "subject", e.target.value)}
                        placeholder="Leave blank to use the default"
                        data-testid={`sb-emails-subject-${selectedKind}`}
                        className="rounded-none h-8 text-xs" disabled={!canManage} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">HTML body</Label>
              <Textarea value={tpl.html || ""}
                            onChange={(e) => patchTemplate(selectedKind, "html", e.target.value)}
                            placeholder="Leave blank to use the default warm-luxe template"
                            data-testid={`sb-emails-html-${selectedKind}`}
                            rows={12}
                            className="rounded-none text-[11px] font-mono" disabled={!canManage} />
              <div className="text-[10px] text-muted-foreground mt-1 flex flex-wrap gap-1">
                <span className="uppercase tracking-widest font-semibold">Placeholders:</span>
                {(data.placeholders || []).map((p) => (
                  <button key={p} type="button"
                          onClick={() => patchTemplate(selectedKind, "html", (tpl.html || "") + `{{${p}}}`)}
                          className="font-mono border border-border px-1 hover:bg-primary/10">
                    {`{{${p}}}`}
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">
                Tip: <code className="font-mono bg-muted px-1">{"{{tracking_url}}"}</code> renders as a clickable link customers can open to see their live booking status.
              </div>
            </div>

            {/* Test-send row */}
            <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-border/60">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Test:</div>
              <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                       placeholder="you@yourstudio.com"
                       data-testid="sb-emails-test-to"
                       className="rounded-none h-7 text-xs w-56" />
              <Button size="sm" onClick={testSend}
                        data-testid="sb-emails-test-send"
                        className="rounded-none h-7 text-[10px] uppercase tracking-widest">
                <Send className="w-3 h-3 mr-1" /> Send test
              </Button>
              <div className="text-[10px] text-muted-foreground">Uses saved template + sample tokens.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl rounded-none" data-testid="sb-emails-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-4 h-4" /> Email preview</DialogTitle>
            <DialogDescription className="text-[11px]">Rendered with sample tokens.</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Subject</div>
              <div className="border border-border p-2 text-xs font-semibold" data-testid="sb-emails-preview-subject">{preview.subject}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Body</div>
              <div className="border border-border p-3 max-h-[420px] overflow-auto" data-testid="sb-emails-preview-body"
                     dangerouslySetInnerHTML={{ __html: preview.html }} />
            </div>
          )}
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setPreviewOpen(false)} className="rounded-none">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─── Feb 2026 · R280 · Reusable chips input for email lists ──────────
// Oct 2026 · Generalised with optional `validator` + `normalizer` so the
// same component now handles email AND phone-number chips (studio WA
// internal notify list). Defaults match the original email behaviour.
function ChipsEmailInput({ value, onChange, placeholder, testid, disabled, validator, normalizer }) {
  const [buf, setBuf] = useState("");
  const list = Array.isArray(value) ? value : [];
  const _validator = validator || ((p) => p.includes("@"));
  const _normalizer = normalizer || ((p) => p.toLowerCase());
  const commit = () => {
    const parts = buf.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean).map(_normalizer);
    if (!parts.length) return;
    const valid = parts.filter(_validator);
    const next = Array.from(new Set([...list, ...valid]));
    onChange(next);
    setBuf("");
  };
  const remove = (email) => {
    onChange(list.filter((e) => e !== email));
  };
  return (
    <div className="border border-border p-2 flex flex-wrap gap-1.5 min-h-[40px] bg-white" data-testid={testid}>
      {list.map((e) => (
        <span key={e} className="inline-flex items-center gap-1 text-[11px] bg-amber-50 border border-amber-200 px-2 py-0.5">
          {e}
          {!disabled && (
            <button type="button" onClick={() => remove(e)}
                    data-testid={`${testid}-remove-${e}`}
                    className="text-muted-foreground hover:text-rose-600">
              <XIcon className="w-3 h-3" />
            </button>
          )}
        </span>
      ))}
      <input value={buf}
              onChange={(e) => setBuf(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(); }
                if (e.key === "Backspace" && !buf && list.length) remove(list[list.length - 1]);
              }}
              onBlur={commit}
              placeholder={list.length ? "" : (placeholder || "email@example.com")}
              disabled={disabled}
              data-testid={`${testid}-input`}
              className="flex-1 min-w-[140px] text-xs outline-none bg-transparent px-1" />
    </div>
  );
}


// Feb 2026 · WhatsApp template card — holds the template-name input,
// an editable template body (what the admin pastes into the Interakt
// dashboard), the variable-chip legend, Live Preview bubble, and the
// Copy / Reset / Submit-to-Interakt actions.
function WaTemplateCard({
  label, testid, placeholderName, placeholderBody,
  nameValue, onNameChange, bodyValue, onBodyChange, defaultBody, canManage,
}) {
  const bodyRef = useRef(null);
  const insertVariable = (token) => {
    const el = bodyRef.current;
    if (!el) { onBodyChange((bodyValue || "") + " " + token); return; }
    const start = el.selectionStart ?? bodyValue.length;
    const end = el.selectionEnd ?? bodyValue.length;
    const next = bodyValue.slice(0, start) + token + bodyValue.slice(end);
    onBodyChange(next);
    requestAnimationFrame(() => {
      try {
        el.focus();
        const caret = start + token.length;
        el.setSelectionRange(caret, caret);
      } catch (_) {}
    });
  };
  const isDefault = (bodyValue || "").trim() === (defaultBody || "").trim();
  // Feb 2026 · Open Interakt's new-template page and simultaneously
  // copy the body to clipboard. Interakt's dashboard doesn't accept
  // URL-prefill for template content, so this is the cleanest
  // one-click experience: admin lands on the Interakt form with the
  // body already in their clipboard and just hits paste.
  const submitToInterakt = async () => {
    if (!bodyValue?.trim()) {
      toast.error("Add template body first");
      return;
    }
    const ok = await copyToClipboard(bodyValue,
      "Body copied — paste it into Interakt's Message field");
    // Open the new-template page regardless so the admin lands in the
    // right place even if clipboard permissions were denied.
    try { window.open(INTERAKT_NEW_TEMPLATE_URL, "_blank", "noopener,noreferrer"); }
    catch (_) {
      if (!ok) toast.error("Open https://app.interakt.ai/ and paste the body manually");
    }
  };
  const preview = renderWithSamples(bodyValue);
  return (
    <div className="border border-border p-3 bg-background/50" data-testid={`sb-wa-tpl-card-${testid}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <Label className="text-xs font-semibold">{label}</Label>
        <div className="flex items-center gap-1 flex-wrap justify-end">
          {!isDefault && (
            <button
              type="button"
              onClick={() => onBodyChange(defaultBody)}
              disabled={!canManage}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground border border-border px-1.5 py-0.5 cursor-pointer disabled:opacity-50"
              data-testid={`sb-wa-tpl-${testid}-reset`}
              title="Reset to the suggested default body"
            >
              <RefreshCw className="w-3 h-3" /> Reset
            </button>
          )}
          <button
            type="button"
            onClick={() => copyWaTemplateBody(bodyValue)}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground border border-border px-1.5 py-0.5 cursor-pointer"
            data-testid={`sb-wa-tpl-${testid}-copy`}
            title="Copy the exact body — paste it into your Interakt dashboard"
          >
            <Copy className="w-3 h-3" /> Copy body
          </button>
          <button
            type="button"
            onClick={submitToInterakt}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-900 hover:text-emerald-700 border border-emerald-700 bg-emerald-50 px-1.5 py-0.5 cursor-pointer"
            data-testid={`sb-wa-tpl-${testid}-submit-interakt`}
            title="Copy the body and open Interakt's new-template page in a new tab"
          >
            <ExternalLink className="w-3 h-3" /> Submit to Interakt
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* ── Left column: editor ──────────────────────────────────── */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Template name (must match Interakt approval)</div>
          <Input
            placeholder={placeholderName}
            value={nameValue}
            onChange={(e) => onNameChange(e.target.value)}
            className="rounded-none h-8 text-xs mb-2"
            disabled={!canManage}
            data-testid={`sb-wa-tpl-${testid}`}
          />
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Template body (what the customer sees)</div>
          <Textarea
            ref={bodyRef}
            placeholder={placeholderBody}
            value={bodyValue}
            onChange={(e) => onBodyChange(e.target.value)}
            className="rounded-none text-xs font-mono leading-relaxed"
            rows={8}
            disabled={!canManage}
            data-testid={`sb-wa-tpl-${testid}-body`}
            maxLength={4096}
          />
          <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-[9px] uppercase tracking-widest text-muted-foreground mr-1">Insert:</span>
              {WA_VARIABLE_LEGEND.map((v) => (
                <button
                  key={v.token}
                  type="button"
                  onClick={() => insertVariable(v.token)}
                  disabled={!canManage}
                  className="text-[10px] font-mono px-1.5 py-0.5 border border-border hover:bg-muted disabled:opacity-50 cursor-pointer"
                  data-testid={`sb-wa-tpl-${testid}-var-${v.token.replace(/[{}]/g, "")}`}
                  title={`${v.label} · sample: ${v.sample}`}
                >
                  {v.token}
                </button>
              ))}
            </div>
            <span className="text-[9px] text-muted-foreground">
              {(bodyValue || "").length}/4096
            </span>
          </div>
          <details className="mt-1 text-[10px] text-muted-foreground">
            <summary className="cursor-pointer select-none hover:text-foreground">What does each variable mean?</summary>
            <ul className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 pl-3">
              {WA_VARIABLE_LEGEND.map((v) => (
                <li key={v.token}>
                  <span className="font-mono">{v.token}</span> — {v.label}
                  <span className="text-muted-foreground"> · <i>{v.sample}</i></span>
                </li>
              ))}
            </ul>
          </details>
        </div>
        {/* ── Right column: live preview bubble ─────────────────────── */}
        <div data-testid={`sb-wa-tpl-${testid}-preview-pane`}>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1">
            <Eye className="w-3 h-3" /> Live preview · sample data
          </div>
          <div className="bg-[#E5DDD4] p-3 min-h-[232px]">
            <div className="bg-[#DCF8C6] text-[13px] leading-[1.45] p-3 relative shadow-sm font-sans whitespace-pre-wrap break-words"
                 style={{
                   borderRadius: "8px 8px 8px 0",
                   fontFamily: "'Segoe UI','Helvetica Neue',Helvetica,Arial,sans-serif",
                   color: "#111",
                   maxWidth: "92%",
                 }}
                 data-testid={`sb-wa-tpl-${testid}-preview`}>
              {preview || <span className="text-muted-foreground italic">Add body text above to see the live preview here.</span>}
              {preview && (
                <div className="text-[10px] text-right mt-1 text-[#667781]">
                  10:42 AM &nbsp;✓✓
                </div>
              )}
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
            Sample values are hard-coded (Alex Mehta, #1234, …) just for preview.
            Real sends substitute live booking values.
          </p>
        </div>
      </div>
    </div>
  );
}
